import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import Lottie from "lottie-react";
import loader from "../assets/loader.json";
import Navbar from "../Components/Navbar";
import { useGetAllTicketsQuery } from "../redux/features/user/userApi";

const Tickets = () => {
  const navigate = useNavigate();
  const { data, isLoading, error } = useGetAllTicketsQuery();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all, open, closed

  const columns = [
    {
      field: "issueType",
      headerName: "Issue Type",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <span className="text-xs sm:text-sm font-medium text-gray-800">
          {params.value || "General"}
        </span>
      ),
    },
    {
      field: "ticketId",
      headerName: "Ticket ID",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <span className="font-mono text-blue-600 font-semibold">
          {params.value}
        </span>
      ),
    },
    {
      field: "subject",
      headerName: "Subject",
      flex: 1.5,
      minWidth: 200,
    },
    {
      field: "userEmail",
      headerName: "User Email",
      flex: 1.2,
      minWidth: 180,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => {

        console.log(`params.value----56-----,Tickets------>`, params.value);

        const status = params.value ;
        const statusConfig = {
          open: {
            bg: "bg-yellow-100",
            text: "text-yellow-800",
            border: "border-yellow-300",
            label: "Open",
          },
          closed: {
            bg: "bg-red-100",
            text: "text-black-800",
            border: "border-black-300",
            label: "Closed",
          },
          // pending: {
          //   bg: "bg-blue-100",
          //   text: "text-blue-800",
          //   border: "border-blue-300",
          //   label: "Pending",
          // },
          // resolved: {
          //   bg: "bg-green-100",
          //   text: "text-green-800",
          //   border: "border-green-300",
          //   label: "Resolved",
          // },
        };

        const config = statusConfig[status] || statusConfig.open;

        return (
          <div
            className={`${config.bg} ${config.text} ${config.border}flex items-center justify-center px-3 py-1 rounded-full text-xs font-semibold border`}
          >
            {config.label}
          </div>
        );
      },
    },
    {
      field: "createdAt",
      headerName: "Created At",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "lastUpdated",
      headerName: "Last Updated",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.8,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => (
        <button
          onClick={() => navigate(`/tickets/${params.row.ticketId}`)}
          className="px-4 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 text-sm font-medium"
        >
          View Details
        </button>
      ),
    },
  ];

  const rows = useMemo(() => {
    const result = [];
    if (data?.data || data?.tickets) {
      const tickets = data.data || data.tickets || [];
      tickets.forEach((ticket, index) => {
        const options = {
          hour: "2-digit",
          minute: "2-digit",
          day: "numeric",
          year: "numeric",
          month: "long",
          
          
        };

        const createdDate = ticket.date
          ? new Date(ticket.date).toLocaleDateString("en-US", options)
          : "N/A";
        const updatedDate = ticket.udate
          ? new Date(ticket.udate).toLocaleDateString(
            "en-US",
            options
          )
          : createdDate;
        result.push({
          id: index + 1,
          ticketId: ticket.ticket_id || ticket.ticketId || ticket.id || `TKT-${index + 1}`,
          issueType: ticket.issue_type || ticket.issueType || ticket.category || "General",
          subject: ticket.subject || ticket.title || "No Subject",
          userEmail: ticket.userEmail || ticket.email || ticket.user?.email || "N/A",
          status: ticket.status ? "closed" : "open",
          createdAt: createdDate,
          createdAtRaw: createdDate,
          lastUpdated: updatedDate,
          lastUpdatedRaw: updatedDate
        });
      });
    }
    return result;
  }, [data]);

  // Filter rows based on search and status
  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const matchesSearch =
        row.ticketId?.toLowerCase().includes(search.toLowerCase()) ||
        row.subject?.toLowerCase().includes(search.toLowerCase()) ||
        row.userEmail?.toLowerCase().includes(search.toLowerCase()) ||
        row.issueType?.toLowerCase().includes(search.toLowerCase());

      const matchesStatus =
        statusFilter === "all" ||
        row.status?.toLowerCase() === statusFilter.toLowerCase();

      return matchesSearch && matchesStatus;
    });
  }, [rows, search, statusFilter]);

  const style = {
    height: 100,
    width: 100,
    margin: "auto",
    alignItem: "center",
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Lottie animationData={loader} style={style} loop={true} />
      </div>
    );
  }

  if (error) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-50 pt-20 md:pt-24 pb-8 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
              <p className="text-red-600 font-semibold text-base sm:text-lg">
                Error loading tickets. Please try again later.
              </p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 pt-20 md:pt-24 pb-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="w-full border rounded-lg bg-white shadow-lg overflow-hidden">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 sm:p-6 text-blue-900 font-bold gap-3 sm:gap-4 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold">Support Tickets</h1>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span className="font-semibold">Total:</span>
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full font-bold">
                  {filteredRows.length}
                </span>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="p-4 sm:p-6 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
                <div 
                  onClick={() => setStatusFilter("all")}
                  className={`bg-white rounded-lg p-3 sm:p-4 border shadow-sm cursor-pointer transition-all duration-200 ${
                    statusFilter === "all" 
                      ? "border-blue-400 bg-blue-50 shadow-md transform scale-105" 
                      : "border-blue-200 hover:border-blue-300 hover:shadow-md hover:transform hover:scale-102"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600 font-medium">Total</p>
                      <p className="text-xl sm:text-2xl font-bold text-blue-900">{rows.length}</p>
                    </div>
                    <div className="bg-blue-100 rounded-full p-2 sm:p-3">
                      <svg className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                  </div>
                </div>
                <div 
                  onClick={() => setStatusFilter("open")}
                  className={`bg-white rounded-lg p-3 sm:p-4 border shadow-sm cursor-pointer transition-all duration-200 ${
                    statusFilter === "open" 
                      ? "border-yellow-400 bg-yellow-50 shadow-md transform scale-105" 
                      : "border-yellow-200 hover:border-yellow-300 hover:shadow-md hover:transform hover:scale-102"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600 font-medium">Open</p>
                      <p className="text-xl sm:text-2xl font-bold text-yellow-600">{rows.filter(r => r.status === 'open').length}</p>
                    </div>
                    <div className="bg-yellow-100 rounded-full p-2 sm:p-3">
                      <svg className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                </div>
                <div 
                  onClick={() => setStatusFilter("closed")}
                  className={`bg-white rounded-lg p-3 sm:p-4 border shadow-sm cursor-pointer transition-all duration-200 ${
                    statusFilter === "closed" 
                      ? "border-red-400 bg-red-50 shadow-md transform scale-105" 
                      : "border-red-200 hover:border-red-300 hover:shadow-md hover:transform hover:scale-102"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600 font-medium">Closed</p>
                      <p className="text-xl sm:text-2xl font-bold text-red-600">{rows.filter(r => r.status === 'closed').length}</p>
                    </div>
                    <div className="bg-red-100 rounded-full p-2 sm:p-3">
                      <svg className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center p-4 sm:p-6 gap-4 border-b bg-gray-50">
              <input
                type="text"
                placeholder="Search by Ticket ID, Subject, or Email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="border border-gray-300 px-4 py-2.5 rounded-lg w-full sm:w-auto sm:flex-1 max-w-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-sm sm:text-base"
              />
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full sm:w-auto">
                <span className="font-semibold text-gray-700 text-sm sm:text-base whitespace-nowrap">
                  Status:
                </span>
                <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                  {[
                    { value: "all", label: "All", color: "blue" },
                    { value: "open", label: "Open", color: "yellow" },
                    { value: "closed", label: "Closed", color: "red" },
                    // { value: "pending", label: "Pending", color: "blue" },
                    // { value: "resolved", label: "Resolved", color: "green" },
                  ].map((status) => (
                    <button
                      key={status.value}
                      onClick={() => setStatusFilter(status.value)}
                      className={`px-3 sm:px-4 py-1.5 rounded-lg font-semibold transition-colors duration-150 text-xs sm:text-sm ${
                        statusFilter === status.value
                          ? status.color === "blue"
                            ? "bg-blue-600 text-white shadow"
                            : status.color === "yellow"
                            ? "bg-yellow-600 text-white shadow"
                            : status.color === "red"
                            ? "bg-red-600 text-white shadow"
                            : status.color === "green"
                            ? "bg-green-600 text-white shadow"
                            : "bg-gray-600 text-white shadow"
                          : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                      }`}
                    >
                      {status.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Data Grid */}
            <div className="p-4 sm:p-6 overflow-x-auto">
              {filteredRows.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500 text-base sm:text-lg">
                    {search || statusFilter !== "all"
                      ? "No tickets found matching your criteria."
                      : "No tickets available."}
                  </p>
                </div>
              ) : (
                <div className="w-full overflow-x-auto">
                  <DataGrid
                    columns={columns}
                    rows={filteredRows}
                    pageSize={25}
                    rowsPerPageOptions={[25, 50, 100]}
                    autoHeight
                    sx={{
                      border: "none",
                      minWidth: 600,
                      "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: "#f1f5f9",
                        borderRadius: "8px 8px 0 0",
                        fontWeight: "600",
                      },
                      "& .MuiDataGrid-cell": {
                        borderBottom: "1px solid #e2e8f0",
                        fontSize: "0.875rem",
                      },
                      "& .MuiDataGrid-row:hover": {
                        backgroundColor: "#f8fafc",
                        cursor: "text",
                      },
                      "& .MuiDataGrid-cell:focus": {
                        outline: "none",
                      },
                      "& .MuiDataGrid-columnHeader": {
                        fontSize: "0.875rem",
                      },
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Tickets;
